/**
 * ContentBlocksEditor — Editeur reutilisable pour les blocs editoriaux
 * (Articles + Carnets de voyage/Journees).
 *
 * Types supportes : section_title, paragraph, quote, image, image_gallery, video.
 * Chaque bloc peut etre reordonne (up/down) et supprime.
 * Les blocs image/gallery/video utilisent MediaSelector.
 *
 * Props :
 *   - blocks: array
 *   - onChange: (blocks) => void
 */
import { useState } from "react";
import {
  ArrowUp, ArrowDown, Trash2, Plus, Type, AlignLeft, Quote as QuoteIcon,
  Image as ImageIcon, Images, Video, Search, Sparkles, Loader2,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import MediaSelector from "./MediaSelector";
import { aiBlockAction, aiGenerateCaption } from "../lib/api";

const BLOCK_META = {
  section_title: { icon: Type, label: "Titre" },
  paragraph: { icon: AlignLeft, label: "Paragraphe" },
  quote: { icon: QuoteIcon, label: "Citation" },
  image: { icon: ImageIcon, label: "Image" },
  image_gallery: { icon: Images, label: "Galerie" },
  video: { icon: Video, label: "Vidéo" },
  list: { icon: AlignLeft, label: "Liste" },
  key_figures: { icon: Type, label: "Chiffres clés" },
  callout: { icon: QuoteIcon, label: "Encadré" },
  links: { icon: AlignLeft, label: "Liens / sources" },
};

export default function ContentBlocksEditor({ blocks = [], onChange, targetType = null, targetId = null }) {
  const [pickerFor, setPickerFor] = useState(null); // {index, kind: 'image'|'image_gallery'|'video'}
  const [aiFor, setAiFor] = useState(null); // {index, block, loading, action, proposal, proposals}
  const [captionFor, setCaptionFor] = useState(null); // {index, block, loading, proposal}

  const PARAGRAPH_ACTIONS = [
    { id: "reformulate", label: "Reformuler" },
    { id: "correct", label: "Corriger" },
    { id: "shorten", label: "Raccourcir" },
    { id: "expand", label: "Approfondir (sans inventer)" },
    { id: "improve_style", label: "Améliorer le style" },
    { id: "more_factual", label: "Plus factuel" },
    { id: "regenerate_from_notes", label: "Régénérer depuis les notes" },
  ];

  const update = (i, patch) => {
    const next = [...blocks];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const remove = (i) => {
    const next = blocks.filter((_, idx) => idx !== i);
    onChange(next);
  };
  const move = (i, dir) => {
    const t = i + dir;
    if (t < 0 || t >= blocks.length) return;
    const next = [...blocks];
    [next[i], next[t]] = [next[t], next[i]];
    onChange(next);
  };
  const add = (type) => {
    const initial = { type };
    if (type === "section_title" || type === "paragraph" || type === "quote") initial.text = "";
    if (type === "image") initial.photo_id = null;
    if (type === "image_gallery") initial.photo_ids = [];
    if (type === "video") initial.media_id = null;
    onChange([...blocks, initial]);
  };

  // ===== IA par bloc (Bloc 5) =====
  const runBlockAction = async (index, action, extra_context = null) => {
    if (!targetType || !targetId) {
      toast.error("Contexte IA indisponible (enregistrez l'entité d'abord).");
      return;
    }
    setAiFor({ index, block: blocks[index], loading: true, action });
    try {
      const res = await aiBlockAction({
        block: blocks[index],
        action,
        target_type: targetType,
        target_id: targetId,
        extra_context,
      });
      setAiFor({
        index, block: blocks[index], loading: false, action,
        proposal: res.data.proposal,
        proposals: res.data.proposals,
      });
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Erreur IA";
      toast.error(detail);
      setAiFor(null);
    }
  };

  const applyProposal = (proposalText, proposalType = null) => {
    if (!aiFor) return;
    const i = aiFor.index;
    const next = [...blocks];
    next[i] = { ...next[i], type: proposalType || next[i].type, text: proposalText };
    onChange(next);
    setAiFor(null);
    toast.success("Proposition appliquée.");
  };

  const runCaptionForBlock = async (index) => {
    const b = blocks[index];
    const mediaId = b.photo_id || b.media_id || (b.photo_ids && b.photo_ids[0]);
    if (!mediaId) return toast.error("Aucun média rattaché à ce bloc.");
    setCaptionFor({ index, loading: true });
    try {
      const res = await aiGenerateCaption(mediaId, {});
      setCaptionFor({ index, loading: false, proposal: res.data.caption });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur légende IA");
      setCaptionFor(null);
    }
  };

  const applyCaption = () => {
    if (!captionFor || !captionFor.proposal) return;
    const i = captionFor.index;
    const next = [...blocks];
    const current = next[i].caption || "";
    if (current.trim() && !window.confirm("Écraser la légende existante ?")) {
      setCaptionFor(null);
      return;
    }
    next[i] = { ...next[i], caption: captionFor.proposal };
    onChange(next);
    setCaptionFor(null);
    toast.success("Légende appliquée.");
  };

  const onMediaSelected = (media) => {
    if (!pickerFor) return;
    const { index, kind } = pickerFor;
    if (kind === "image" || kind === "video") {
      const single = Array.isArray(media) ? media[0] : media;
      if (!single) return;
      const patch = kind === "image"
        ? { photo_id: single.id, photo_url: single.public_cloudinary_url || single.original_cloudinary_url }
        : { media_id: single.id, video_url: single.public_cloudinary_url || single.original_cloudinary_url, poster_url: single.poster_url };
      update(index, patch);
    } else if (kind === "image_gallery") {
      const list = Array.isArray(media) ? media : [media];
      const ids = list.map((m) => m.id);
      const urls = list.map((m) => m.public_cloudinary_url || m.original_cloudinary_url);
      update(index, { photo_ids: [...(blocks[index].photo_ids || []), ...ids], photo_urls: [...(blocks[index].photo_urls || []), ...urls] });
    }
    setPickerFor(null);
  };

  return (
    <div className="space-y-3" data-testid="content-blocks-editor">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 p-3 bg-secondary/50 rounded border border-border">
        <span className="text-sm font-semibold text-primary mr-2 self-center">Ajouter :</span>
        {Object.entries(BLOCK_META).map(([type, meta]) => {
          const Icon = meta.icon;
          return (
            <Button
              key={type}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => add(type)}
              className="gap-1"
              data-testid={`add-block-${type}`}
            >
              <Icon className="h-3.5 w-3.5" /> {meta.label}
            </Button>
          );
        })}
      </div>

      {/* Blocks list */}
      {blocks.length === 0 ? (
        <div className="text-sm text-muted-foreground text-center py-6 border border-dashed border-border rounded">
          Aucun bloc pour l'instant.
        </div>
      ) : (
        blocks.map((b, i) => {
          const meta = BLOCK_META[b.type] || BLOCK_META.paragraph;
          const Icon = meta.icon;
          return (
            <div key={i} className="p-3 border border-border rounded bg-white" data-testid={`block-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wide">
                  <Icon className="h-3.5 w-3.5" /> {meta.label} <span className="text-muted-foreground normal-case">#{i + 1}</span>
                </div>
                <div className="flex gap-1">
                  {targetType && targetId && (b.type === "paragraph" || b.type === "quote") && (
                    <div className="relative group">
                      <Button type="button" variant="ghost" size="icon" title="Actions IA" data-testid={`block-${i}-ai-btn`}>
                        <Sparkles className="h-4 w-4 text-accent" />
                      </Button>
                      <div className="absolute right-0 top-full mt-1 hidden group-hover:block group-focus-within:block z-20 min-w-[220px] bg-white border border-border rounded shadow-lg py-1">
                        {PARAGRAPH_ACTIONS.map((a) => (
                          <button
                            key={a.id}
                            type="button"
                            onClick={() => runBlockAction(i, a.id)}
                            className="block w-full text-left px-3 py-1.5 text-xs hover:bg-secondary/50"
                            data-testid={`block-${i}-ai-${a.id}`}
                          >{a.label}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  {targetType && targetId && b.type === "section_title" && (
                    <Button
                      type="button" variant="ghost" size="icon" title="Suggérer d'autres titres"
                      onClick={() => runBlockAction(i, "suggest_titles")}
                      data-testid={`block-${i}-ai-btn`}
                    >
                      <Sparkles className="h-4 w-4 text-accent" />
                    </Button>
                  )}
                  {targetType && targetId && (b.type === "image" || b.type === "image_gallery") && (b.photo_id || b.media_id || (b.photo_ids || []).length > 0) && (
                    <Button
                      type="button" variant="ghost" size="icon" title="Proposer une légende IA"
                      onClick={() => runCaptionForBlock(i)}
                      data-testid={`block-${i}-ai-caption`}
                    >
                      <Sparkles className="h-4 w-4 text-accent" />
                    </Button>
                  )}
                  <Button type="button" variant="ghost" size="icon" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUp className="h-4 w-4" /></Button>
                  <Button type="button" variant="ghost" size="icon" onClick={() => move(i, 1)} disabled={i === blocks.length - 1}><ArrowDown className="h-4 w-4" /></Button>
                  <Button type="button" variant="ghost" size="icon" onClick={() => remove(i)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                </div>
              </div>

              {(b.type === "section_title") && (
                <Input value={b.text || ""} onChange={(e) => update(i, { text: e.target.value })} placeholder="Titre de section" data-testid={`input-block-${i}`} />
              )}
              {(b.type === "callout" || b.type === "list" || b.type === "links") && (
                <Input value={b.title || ""} onChange={(e) => update(i, { title: e.target.value })} placeholder="Titre du bloc" className="mb-2" data-testid={`input-block-title-${i}`} />
              )}
              {b.type === "callout" && (
                <Textarea rows={3} value={b.text || ""} onChange={(e) => update(i, { text: e.target.value })} data-testid={`input-block-${i}`} />
              )}
              {b.type === "list" && (
                <Textarea rows={4} value={(b.items || []).join("\n")} onChange={(e) => update(i, { items: e.target.value.split("\n") })} placeholder="Un élément par ligne" data-testid={`input-block-${i}`} />
              )}
              {b.type === "key_figures" && (
                <Textarea rows={3} value={(b.items || []).map((x) => `${x.value} | ${x.label}`).join("\n")} placeholder="valeur | libellé"
                  onChange={(e) => update(i, { items: e.target.value.split("\n").map((l) => { const [value, ...r] = l.split("|"); return { value: (value || "").trim(), label: r.join("|").trim() }; }) })} data-testid={`input-block-${i}`} />
              )}
              {b.type === "links" && (
                <Textarea rows={3} value={(b.links || []).map((x) => `${x.label} | ${x.url}`).join("\n")} placeholder="libellé | url"
                  onChange={(e) => update(i, { links: e.target.value.split("\n").map((l) => { const k = l.lastIndexOf("|"); return k < 0 ? { label: l.trim(), url: "" } : { label: l.slice(0, k).trim(), url: l.slice(k + 1).trim() }; }) })} data-testid={`input-block-${i}`} />
              )}
              {(b.type === "paragraph" || b.type === "quote") && (
                <Textarea
                  rows={b.type === "quote" ? 3 : 5}
                  value={b.text || ""}
                  onChange={(e) => update(i, { text: e.target.value })}
                  placeholder={b.type === "quote" ? "Citation..." : "Votre texte..."}
                  style={{ minHeight: "80px", maxHeight: "400px", overflow: "auto" }}
                  data-testid={`input-block-${i}`}
                />
              )}
              {b.type === "image" && (
                <div className="space-y-2">
                  {(b.photo_url || b.photo_id) ? (
                    <div className="flex items-start gap-3">
                      {b.photo_url && <img src={b.photo_url} alt="" className="w-32 h-32 object-cover rounded border" />}
                      <div className="flex-1 text-xs text-muted-foreground">
                        <div>photo_id : <code>{(b.photo_id || "").slice(0, 12)}...</code></div>
                        <Input placeholder="Légende (optionnel)" value={b.caption || ""} onChange={(e) => update(i, { caption: e.target.value })} className="mt-2" />
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Aucune image sélectionnée</p>
                  )}
                  <Button type="button" variant="outline" size="sm" onClick={() => setPickerFor({ index: i, kind: "image" })} className="gap-1" data-testid={`block-${i}-pick-media`}>
                    <Search className="h-3.5 w-3.5" /> {b.photo_id ? "Changer" : "Choisir un média"}
                  </Button>
                </div>
              )}
              {b.type === "image_gallery" && (
                <div className="space-y-2">
                  <div className="grid grid-cols-4 gap-2">
                    {(b.photo_urls || []).map((url, idx) => (
                      <div key={idx} className="relative">
                        <img src={url} alt="" className="w-full aspect-square object-cover rounded border" />
                        <button
                          type="button"
                          onClick={() => {
                            const newIds = (b.photo_ids || []).filter((_, k) => k !== idx);
                            const newUrls = (b.photo_urls || []).filter((_, k) => k !== idx);
                            update(i, { photo_ids: newIds, photo_urls: newUrls });
                          }}
                          className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                        >×</button>
                      </div>
                    ))}
                  </div>
                  <div className="text-xs text-muted-foreground">{(b.photo_ids || []).length} image(s) dans la galerie</div>
                  <Button type="button" variant="outline" size="sm" onClick={() => setPickerFor({ index: i, kind: "image_gallery" })} className="gap-1">
                    <Search className="h-3.5 w-3.5" /> Ajouter des images
                  </Button>
                </div>
              )}
              {b.type === "video" && (
                <div className="space-y-2">
                  {b.video_url ? (
                    <video src={b.video_url} poster={b.poster_url} controls className="w-full max-w-md rounded border" />
                  ) : (
                    <p className="text-sm text-muted-foreground">Aucune vidéo sélectionnée</p>
                  )}
                  <Button type="button" variant="outline" size="sm" onClick={() => setPickerFor({ index: i, kind: "video" })} className="gap-1">
                    <Search className="h-3.5 w-3.5" /> {b.media_id ? "Changer" : "Choisir une vidéo"}
                  </Button>
                </div>
              )}
            </div>
          );
        })
      )}

      {pickerFor && (
        <MediaSelector
          open={!!pickerFor}
          onOpenChange={(v) => !v && setPickerFor(null)}
          onSelect={onMediaSelected}
          multiple={pickerFor.kind === "image_gallery"}
          type={pickerFor.kind === "video" ? "video" : "photo"}
        />
      )}

      {/* Modale IA par bloc — Avant / Après (Bloc 5) */}
      <Dialog open={!!aiFor} onOpenChange={(o) => !o && setAiFor(null)}>
        <DialogContent data-testid="ai-block-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Proposition IA — action « {aiFor?.action} »</DialogTitle>
          </DialogHeader>
          {aiFor?.loading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
              L'IA travaille sur ce bloc...
            </div>
          ) : aiFor?.proposals ? (
            <div className="space-y-2 py-2">
              <div className="text-xs text-muted-foreground">Choisissez une des propositions :</div>
              {aiFor.proposals.map((p, k) => (
                <button
                  key={k} type="button"
                  onClick={() => applyProposal(p.text, p.type)}
                  className="w-full text-left p-3 border border-border rounded hover:bg-accent/10 hover:border-accent"
                  data-testid={`ai-proposal-${k}`}
                >{p.text}</button>
              ))}
            </div>
          ) : aiFor?.proposal ? (
            <div className="space-y-3 py-2">
              <div>
                <div className="text-xs font-semibold text-muted-foreground mb-1">Avant</div>
                <div className="text-sm p-2 bg-secondary/30 rounded border border-border whitespace-pre-wrap">{aiFor.block?.text || "(vide)"}</div>
              </div>
              <div>
                <div className="text-xs font-semibold text-accent mb-1">Après (IA)</div>
                <div className="text-sm p-2 bg-accent/5 rounded border border-accent/40 whitespace-pre-wrap" data-testid="ai-proposal-text">{aiFor.proposal.text}</div>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiFor(null)}>Annuler</Button>
            {aiFor?.proposal && !aiFor.loading && (
              <>
                <Button variant="outline" onClick={() => runBlockAction(aiFor.index, aiFor.action)} data-testid="ai-regenerate">Régénérer</Button>
                <Button onClick={() => applyProposal(aiFor.proposal.text, aiFor.proposal.type)} data-testid="ai-apply">Appliquer</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modale légende IA */}
      <Dialog open={!!captionFor} onOpenChange={(o) => !o && setCaptionFor(null)}>
        <DialogContent data-testid="ai-caption-dialog">
          <DialogHeader>
            <DialogTitle>Légende proposée par l'IA</DialogTitle>
          </DialogHeader>
          {captionFor?.loading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Génération...
            </div>
          ) : (
            <div className="py-2 space-y-2">
              <div className="text-xs text-muted-foreground">Légende actuelle :</div>
              <div className="text-sm p-2 bg-secondary/30 rounded border border-border">
                {(blocks[captionFor?.index]?.caption) || "(aucune)"}
              </div>
              <div className="text-xs text-accent">Proposition IA :</div>
              <div className="text-sm p-2 bg-accent/5 rounded border border-accent/40" data-testid="ai-caption-proposal">
                {captionFor?.proposal || "(vide)"}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCaptionFor(null)}>Annuler</Button>
            {captionFor?.proposal && !captionFor.loading && (
              <Button onClick={applyCaption} data-testid="ai-caption-apply">Appliquer</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
